import { useMemo } from "react"
import { useQuery } from "@rocicorp/zero/react"

import type { AgentCodeFix } from "@productlane/zero/queries"
import type { AgentRecommendation } from "@productlane/zero/schema"
import { hasEnabledEmail } from "@productlane/lib/src/email-settings"
import { queries } from "@productlane/zero/queries"

import type { RawProposal } from "../helpCenter/aggregate-proposals"
import type { AgentDocsGroup, AgentItem } from "./agent-items"
import type { AgentSetupStep } from "./agent-setup"
import { useIsAgentPageAvailable } from "@/lib/hooks/use-is-agent-page-available"
import { hasLinearConnection } from "@/lib/linear-connection"
import { useZeroWorkspace } from "@/lib/zero"
import { aggregateProposals } from "../helpCenter/aggregate-proposals"
import { actionableCount, buildAgentItems } from "./agent-items"
import { openSetupSteps, widgetStepDone } from "./agent-setup"
import { useAgentSetupDismissals } from "./agent-setup-dismissals"

export function useOpenAgentRecommendations(): {
	rows: readonly AgentRecommendation[]
	loaded: boolean
} {
	const available = useIsAgentPageAvailable()
	const [rows, result] = useQuery(queries.openAgentRecommendations(), {
		enabled: available === true,
	})
	return { rows, loaded: result.type === "complete" }
}

// Docs proposals behind an open Agent card are reviewed there, so the Docs
// sidebar leaves them out.
export function useAgentOwnedProposalGroupIds(): ReadonlySet<string> {
	const { rows } = useOpenAgentRecommendations()
	return useMemo(
		() =>
			new Set(
				rows
					.map((row) => row.proposalGroupId)
					.filter((id): id is string => !!id),
			),
		[rows],
	)
}

// Shared with the Docs page, which syncs the same query.
export function useOpenDocsGroups(): {
	rows: readonly AgentDocsGroup[]
	loaded: boolean
} {
	const available = useIsAgentPageAvailable()
	const [rows, result] = useQuery(queries.openProposalGroupsForWorkspace(), {
		enabled: available === true,
	})
	return { rows, loaded: result.type === "complete" }
}

// A teammate's suggested edits and API drafts, which belong to no group.
export function useOpenStandaloneProposals(): {
	rows: readonly RawProposal[]
	loaded: boolean
} {
	const available = useIsAgentPageAvailable()
	const [rows, result] = useQuery(queries.openProposalsForWorkspace(), {
		enabled: available === true,
	})
	return {
		rows: rows as unknown as readonly RawProposal[],
		loaded: result.type === "complete",
	}
}

export function useAgentCodeFixes(): {
	rows: readonly AgentCodeFix[]
	loaded: boolean
} {
	const available = useIsAgentPageAvailable()
	const [rows, result] = useQuery(queries.agentCodeFixes(), {
		enabled: available === true,
	})
	return { rows, loaded: result.type === "complete" }
}

// Reads the workspace Zero already holds, plus the one-row widget query the checklist syncs too.
export function useOpenSetupSteps(): {
	steps: readonly AgentSetupStep[]
	loaded: boolean
} {
	const available = useIsAgentPageAvailable()
	const workspace = useZeroWorkspace()
	const [widgetThreads, result] = useQuery(queries.widgetThreads(), {
		enabled: available === true,
	})
	const declined = useAgentSetupDismissals((state) =>
		workspace ? state.byWorkspace[workspace.id] : undefined,
	)
	const widgetKnown = result.type === "complete"
	const loaded = !!workspace && widgetKnown
	// The workspace is already synced, so its steps show on the first frame.
	const steps = useMemo(
		() =>
			workspace
				? openSetupSteps(
						{
							github: !!workspace.githubSettings?.repositoryFullName,
							linear: hasLinearConnection(workspace),
							email: !!hasEnabledEmail(workspace.emailSettings),
							widget: widgetStepDone(widgetThreads.length > 0, widgetKnown),
							domain: !!workspace.customDomain,
						},
						declined,
					)
				: [],
		[workspace, widgetKnown, widgetThreads, declined],
	)
	return { steps, loaded }
}

export function useAgentItems(): {
	items: readonly AgentItem[]
	actionable: number
	loaded: boolean
} {
	const recommendations = useOpenAgentRecommendations()
	const fixes = useAgentCodeFixes()
	const docs = useOpenDocsGroups()
	const standalone = useOpenStandaloneProposals()
	const setup = useOpenSetupSteps()
	const items = useMemo(
		() =>
			buildAgentItems({
				setup: setup.steps,
				recommendations: recommendations.rows,
				fixes: fixes.rows,
				docsGroups: docs.rows,
				docsLoaded: docs.loaded,
				proposalRows: aggregateProposals({
					standalone: standalone.rows,
					groups: [],
				}).rows,
				now: Date.now(),
			}),
		[
			setup.steps,
			recommendations.rows,
			fixes.rows,
			docs.rows,
			docs.loaded,
			standalone.rows,
		],
	)
	return {
		items,
		actionable: actionableCount(items),
		loaded:
			setup.loaded &&
			recommendations.loaded &&
			fixes.loaded &&
			docs.loaded &&
			standalone.loaded,
	}
}
